/**
 * Firestore Sync Layer
 * ────────────────────
 * Connects Zustand stores to Firestore.
 * - When authenticated: reads/writes to /athletes/{uid}/...
 * - In demo mode: localStorage only (no Firestore calls)
 *
 * Each store calls these helpers in its actions.
 * The stores remain the source of truth for the UI (optimistic updates),
 * with Firestore as the persistent backend.
 */
import {
  doc, setDoc, getDoc, getDocs,
  collection, query, where, orderBy, limit,
  onSnapshot, type Unsubscribe,
} from 'firebase/firestore';
import { db, isDemoMode } from './firebase';
import { useAuthStore } from './auth-store';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getUid(): string | null {
  const user = useAuthStore.getState().user;
  return user?.uid ?? null;
}

function isLive(): boolean {
  return !isDemoMode && !!getUid();
}

// ─── Schedule Sync ───────────────────────────────────────────────────────────

/** Write a day's state to Firestore: /athletes/{uid}/schedule/{date} */
export async function syncScheduleDay(date: string, data: Record<string, unknown>): Promise<void> {
  if (!isLive()) return;
  const uid = getUid()!;
  try {
    await setDoc(doc(db, 'athletes', uid, 'schedule', date), {
      ...data,
      updatedAt: Date.now(),
    }, { merge: true });
  } catch (err) {
    console.warn('Firestore schedule sync failed:', err);
  }
}

/** Read a day's state from Firestore */
export async function loadScheduleDay(date: string): Promise<Record<string, unknown> | null> {
  if (!isLive()) return null;
  const uid = getUid()!;
  try {
    const snap = await getDoc(doc(db, 'athletes', uid, 'schedule', date));
    return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  } catch (err) {
    console.warn('Firestore schedule load failed:', err);
    return null;
  }
}

/** Subscribe to real-time updates for a date range */
export function subscribeScheduleRange(
  startDate: string,
  endDate: string,
  onUpdate: (date: string, data: Record<string, unknown>) => void,
): Unsubscribe | null {
  if (!isLive()) return null;
  const uid = getUid()!;
  const q = query(
    collection(db, 'athletes', uid, 'schedule'),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
  );
  return onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added' || change.type === 'modified') {
        const data = change.doc.data() as Record<string, unknown>;
        onUpdate(change.doc.id, data);
      }
    });
  }, (err) => {
    console.warn('Firestore schedule subscription error:', err);
  });
}

// ─── Meals Sync ──────────────────────────────────────────────────────────────

/** Write a meal log to Firestore: /athletes/{uid}/meals/{id} */
export async function syncMealLog(meal: {
  id: string;
  date: string;
  mealWindow: string;
  items: unknown[];
  totalCal: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
  source: string;
  timestamp: number;
}): Promise<void> {
  if (!isLive()) return;
  const uid = getUid()!;
  try {
    await setDoc(doc(db, 'athletes', uid, 'meals', meal.id), {
      ...meal,
      uid,
    });
  } catch (err) {
    console.warn('Firestore meal sync failed:', err);
  }
}

/** Load meals for a specific date from Firestore */
export async function loadMealsForDate(date: string): Promise<Record<string, unknown>[]> {
  if (!isLive()) return [];
  const uid = getUid()!;
  try {
    const q = query(
      collection(db, 'athletes', uid, 'meals'),
      where('date', '==', date),
      orderBy('timestamp', 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as Record<string, unknown>);
  } catch (err) {
    console.warn('Firestore meals load failed:', err);
    return [];
  }
}

/** Delete a meal from Firestore */
export async function deleteMealLog(mealId: string): Promise<void> {
  if (!isLive()) return;
  const uid = getUid()!;
  try {
    const { deleteDoc } = await import('firebase/firestore');
    await deleteDoc(doc(db, 'athletes', uid, 'meals', mealId));
  } catch (err) {
    console.warn('Firestore meal delete failed:', err);
  }
}

// ─── Profile Sync ────────────────────────────────────────────────────────────

/** Write user profile to Firestore: /athletes/{uid}/profile */
export async function syncProfile(data: Record<string, unknown>): Promise<void> {
  if (!isLive()) return;
  const uid = getUid()!;
  try {
    await setDoc(doc(db, 'athletes', uid), {
      ...data,
      updatedAt: Date.now(),
    }, { merge: true });
  } catch (err) {
    console.warn('Firestore profile sync failed:', err);
  }
}

/** Load user profile from Firestore */
export async function loadProfile(): Promise<Record<string, unknown> | null> {
  if (!isLive()) return null;
  const uid = getUid()!;
  try {
    const snap = await getDoc(doc(db, 'athletes', uid));
    return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  } catch (err) {
    console.warn('Firestore profile load failed:', err);
    return null;
  }
}

// ─── Streak Sync ──────────────────────────────────────────────────────────────

/**
 * Write streak state patch to Firestore: /athletes/{uid}/streak
 * Called on every check-in, freeze, and weekly reset.
 */
export async function syncStreak(patch: Record<string, unknown>): Promise<void> {
  if (!isLive()) return;
  const uid = getUid()!;
  try {
    await setDoc(doc(db, 'athletes', uid, 'streak', 'current'), {
      ...patch,
      updatedAt: Date.now(),
    }, { merge: true });
  } catch (err) {
    console.warn('Firestore streak sync failed:', err);
  }
}

/**
 * Read streak state from Firestore: /athletes/{uid}/streak/current
 * Used during hydration on login.
 */
export async function loadStreak(): Promise<Record<string, unknown> | null> {
  if (!isLive()) return null;
  const uid = getUid()!;
  try {
    const snap = await getDoc(doc(db, 'athletes', uid, 'streak', 'current'));
    return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  } catch (err) {
    console.warn('Firestore streak load failed:', err);
    return null;
  }
}

// ─── Activities Sync (REQ-SYNC-04) ───────────────────────────────────────────
// Activities are written server-side (Strava/Terra webhooks, CSV write-through).
// The listener's initial snapshot delivers the full window (so it doubles as
// hydration on login) and later snapshots deliver live webhook writes.

/** Subscribe to activity docs on/after startDate, batched per snapshot. */
export function subscribeActivities(
  startDate: string,
  onUpdate: (docs: Record<string, unknown>[]) => void,
): Unsubscribe | null {
  if (!isLive()) return null;
  const uid = getUid()!;
  const q = query(
    collection(db, 'athletes', uid, 'activities'),
    where('date', '>=', startDate),
  );
  return onSnapshot(q, (snapshot) => {
    const changed = snapshot.docChanges()
      .filter(c => c.type === 'added' || c.type === 'modified')
      .map(c => c.doc.data() as Record<string, unknown>);
    if (changed.length > 0) onUpdate(changed);
  }, (err) => {
    console.warn('Firestore activities subscription error:', err);
  });
}

// ─── Niggle Sync (REQ-CHECKIN-04) ────────────────────────────────────────────

/** Record the daily niggle status: /athletes/{uid}/niggles/{date}. 'none' is a valid record. */
export async function syncNiggle(date: string, data: { site: string; source: string }): Promise<void> {
  if (!isLive()) return;
  const uid = getUid()!;
  try {
    await setDoc(doc(db, 'athletes', uid, 'niggles', date), {
      ...data,
      date,
      updatedAt: Date.now(),
    }, { merge: true });
  } catch (err) {
    console.warn('Firestore niggle sync failed:', err);
  }
}

// ─── Weekly Review (REQ-REVIEW-03) ───────────────────────────────────────────

/** Load the stored weekly review for a plan week number: /athletes/{uid}/reviews */
export async function loadReviewForWeek(weekNumber: number): Promise<Record<string, unknown> | null> {
  if (!isLive()) return null;
  const uid = getUid()!;
  try {
    const q = query(
      collection(db, 'athletes', uid, 'reviews'),
      where('weekNumber', '==', weekNumber),
      orderBy('generatedAt', 'desc'),
      limit(1),
    );
    const snap = await getDocs(q);
    return snap.empty ? null : (snap.docs[0]!.data() as Record<string, unknown>);
  } catch (err) {
    console.warn('Firestore review load failed:', err);
    return null;
  }
}

// ─── Oversight (REQ-REVIEW-04) ───────────────────────────────────────────────

/** Load pending oversight items (plan adjustments, load-tolerance flags). */
export async function loadPendingOversight(): Promise<{ id: string; data: Record<string, unknown> }[]> {
  if (!isLive()) return [];
  const uid = getUid()!;
  try {
    const q = query(
      collection(db, 'athletes', uid, 'oversight'),
      where('status', '==', 'pending'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, data: d.data() as Record<string, unknown> }));
  } catch (err) {
    console.warn('Firestore oversight load failed:', err);
    return [];
  }
}

/** Accept or decline an oversight item. */
export async function respondToOversight(id: string, status: 'accepted' | 'declined'): Promise<void> {
  if (!isLive()) return;
  const uid = getUid()!;
  try {
    await setDoc(doc(db, 'athletes', uid, 'oversight', id), {
      status,
      respondedAt: Date.now(),
    }, { merge: true });
  } catch (err) {
    console.warn('Firestore oversight update failed:', err);
  }
}
